import {
  IntegrationEnhanced,
  useOrgIntegrationConnections,
} from "@/api/integrations";
import {
  BackRow,
  chipButtonClassName,
  EmptySearch,
  EmptyState,
  GroupLabel,
  LcBadge,
  PaneBody,
  PaneHead,
  Row,
  smButtonClassName,
} from "@/components/AdminPanel/Organization/components/manage-pane-primitives";
import { ConnectionContent } from "@/components/ConnectionContent";
import { RequestAccessDialog } from "@/components/RequestAccessDialog";
import { Button } from "@/components/ui/button";
import { useSettingsInDialog } from "@/context/SettingsInDialogContext";
import { useSettingsInitialNavigation } from "@/context/SettingsInitialNavigationContext";
import { useConnectionsSearch } from "@/hooks/useConnectionsSearch";
import { cn } from "@/utils";
import { parseIntegrationIdFromPath } from "@/utils/settingsNavigation";
import { IntegrationIcon, Spinner } from "@cohesive/ui-components";
import { Check } from "lucide-react";
import React, { useEffect, useMemo, useRef, useState } from "react";

/**
 * Connectors — the roster. One row per integration in the source's own mark,
 * grouped by what you can do with it: what's connected, what's available, and
 * what an admin has to let you have.
 *
 * The rail's scope narrows the roster; opening a row drills into the
 * connection in place (deep links land there directly).
 */

interface MyConnectionsProps {
  context?: "page" | "pane";
  /** Rail scope: "All" | "Connected" | "Available". */
  scope?: string;
}

const MyConnections: React.FC<MyConnectionsProps> = ({
  context = "page",
  scope = "All",
}) => {
  const isInDialog = useSettingsInDialog();
  const { pathname: initialPathname } = useSettingsInitialNavigation();
  const initialConnectorsIntegrationId =
    parseIntegrationIdFromPath(initialPathname);
  const [selectedIntegrationId, setSelectedIntegrationId] = useState<
    string | null
  >(null);
  const [requestTarget, setRequestTarget] =
    useState<IntegrationEnhanced | null>(null);
  const [requested, setRequested] = useState<Set<string>>(new Set());

  useEffect(() => {
    setSelectedIntegrationId(
      isInDialog && initialConnectorsIntegrationId
        ? initialConnectorsIntegrationId
        : null,
    );
  }, [isInDialog, initialConnectorsIntegrationId]);

  // A rail scope change is a fresh view of the roster, not a drill-in — but
  // only a CHANGE: keyed on the last scope seen, so neither the first render
  // nor StrictMode's re-run of the mount effects wipes the connector a deep
  // link just opened (ROUND2 sweep).
  const lastScopeRef = useRef(scope);
  useEffect(() => {
    if (lastScopeRef.current === scope) return;
    lastScopeRef.current = scope;
    setSelectedIntegrationId(null);
  }, [scope]);

  const {
    searchTerm,
    setSearchTerm,
    discoverableIntegrations,
    connectedIntegrations,
    availableIntegrations,
    handleIntegrationConnect,
    handleIntegrationClick: openConnectorSettings,
    handleClearFilters,
    getActionType,
    isLoading,
  } = useConnectionsSearch({ context });

  const { data: connections } = useOrgIntegrationConnections();

  /** Connection health, by integration — the API tracks it; show it. */
  const healthByIntegration = useMemo(() => {
    const map = new Map<string, { status?: string; detail?: string }>();
    for (const connection of connections ?? []) {
      map.set(connection.integration_id, {
        status: connection.status,
        detail: connection.status_detail,
      });
    }
    return map;
  }, [connections]);

  const openConnector = (integration: IntegrationEnhanced) => {
    if (isInDialog) {
      setSelectedIntegrationId(integration.id);
      return;
    }
    openConnectorSettings(integration);
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-16">
        <Spinner />
      </div>
    );
  }

  if (isInDialog && selectedIntegrationId) {
    return (
      // `gap-6` matches the design system's detail view, where the back row
      // stands a full step clear of the masthead. It was flush before.
      <div className="flex min-h-0 flex-col gap-6">
        <BackRow
          label="All connectors"
          onBack={() => setSelectedIntegrationId(null)}
        />
        <ConnectionContent
          integrationId={selectedIntegrationId}
          context="pane"
          showHeader={true}
        />
      </div>
    );
  }

  const connectorRow = (
    integration: IntegrationEnhanced,
    kind: "connected" | "available" | "discoverable",
  ) => {
    const actionType = getActionType(
      integration,
      kind === "discoverable" ? "request access" : undefined,
    );
    const health = healthByIntegration.get(integration.id);
    const isOrgWide = integration.auth_level === "organization";
    const hasError = kind === "connected" && health?.status === "error";

    const meta = hasError
      ? (health?.detail ?? "Needs attention — reconnect to keep it working")
      : kind === "connected"
        ? isOrgWide
          ? "Connected · managed by your organization"
          : "Connected"
        : integration.description;

    return (
      <Row
        key={integration.id}
        tile={
          <IntegrationIcon
            imageURL={integration.metadata?.image_url}
            platform={integration.platform}
            className="size-5"
          />
        }
        name={integration.name}
        meta={meta}
        onOpen={() => openConnector(integration)}
      >
        {hasError && <LcBadge tone="blocked">Error</LcBadge>}
        {kind === "connected" ? (
          <Button
            variant="ghost"
            size="sm"
            className={chipButtonClassName}
            onClick={() => openConnector(integration)}
          >
            Manage
          </Button>
        ) : kind === "discoverable" ? (
          requested.has(integration.id) ? (
            <span className="inline-flex shrink-0 items-center gap-1.5 pr-1 text-sm text-n-50">
              <Check className="size-3.5" />
              Requested
            </span>
          ) : (
            <Button
              variant="ghost"
              size="sm"
              className={chipButtonClassName}
              onClick={() => setRequestTarget(integration)}
            >
              Request access
            </Button>
          )
        ) : actionType === "contact-admin" ? (
          <span className="shrink-0 px-2 text-sm text-n-50">Ask an admin</span>
        ) : (
          <Button
            variant="secondary"
            size="sm"
            className={cn("shrink-0", smButtonClassName)}
            onClick={() => handleIntegrationConnect(integration)}
          >
            Connect
          </Button>
        )}
      </Row>
    );
  };

  const showConnected = scope === "All" || scope === "Connected";
  const showAvailable = scope === "All" || scope === "Available";
  const groups: { head: string; rows: React.ReactNode[] }[] = [];

  if (showConnected && connectedIntegrations.length > 0) {
    groups.push({
      head: "Connected",
      rows: connectedIntegrations.map((i) => connectorRow(i, "connected")),
    });
  }
  if (showAvailable && availableIntegrations.length > 0) {
    groups.push({
      head: "Available",
      rows: availableIntegrations.map((i) => connectorRow(i, "available")),
    });
  }
  if (showAvailable && discoverableIntegrations.length > 0) {
    groups.push({
      head: "Request access",
      rows: discoverableIntegrations.map((i) =>
        connectorRow(i, "discoverable"),
      ),
    });
  }

  return (
    <PaneBody>
      <PaneHead
        value={searchTerm}
        onChange={setSearchTerm}
        placeholder="Search connectors"
      />

      {groups.length === 0 ? (
        searchTerm ? (
          <EmptySearch
            query={searchTerm}
            noun="connectors"
            onClear={handleClearFilters}
          />
        ) : (
          <EmptyState
            title={
              scope === "Connected"
                ? "Nothing connected yet."
                : "No connectors here."
            }
            description={
              scope === "Connected"
                ? "Connect a source and it shows up here."
                : undefined
            }
          />
        )
      ) : (
        groups.map((group) => (
          // No `mt-6` here: these sections are direct children of `PaneBody`,
          // whose `gap-6` already spaces them. The old `first:mt-0` never
          // matched either — `PaneHead` is the real first child — so the top
          // group carried a margin *and* the gap, double every other pane's.
          <section key={group.head} className="flex flex-col gap-0.5">
            {/* One group reads as the list itself — no head needed. */}
            {(scope === "All" || groups.length > 1) && (
              <GroupLabel>{group.head}</GroupLabel>
            )}
            {group.rows}
          </section>
        ))
      )}

      {requestTarget && (
        <RequestAccessDialog
          open
          integration={requestTarget}
          onRequested={() =>
            setRequested((prev) => new Set(prev).add(requestTarget.id))
          }
          onOpenChange={(open) => {
            if (!open) setRequestTarget(null);
          }}
        />
      )}
    </PaneBody>
  );
};

export default MyConnections;
